import { AsyncPipe } from '@angular/common';
import { ChangeDetectionStrategy, Component, HostBinding, InjectionToken, Input, OnChanges, OnDestroy, forwardRef, inject } from '@angular/core';

import { ISimpleChanges } from '@frontend/sports/common/core/utils/simple-change';
import { CompetitionRouteService } from '@frontend/sports/competition-list/feature';
import { GridGrouping, SubscriptionTopic } from '@frontend/sports/grid/core/feature/model';
import { GridOption, ObservableGrid, ObservableGridFactory } from '@frontend/sports/grid/core/feature/observables';
import { optionContextServiceProviderFactory } from '@frontend/sports/price-boost/feature';
import { CompetitionPriceBoostsPayload } from '@frontend/sports/types/components/widget/types';
import { ModularConfigAccessorService } from '@frontend/sports/widget/core/feature';

import { GridListComponent } from '../../grid/grid-list.component';
import { PriceBoostService } from '../services/priceboost.service';

export const BAB_TOOLTIP_HOST_INJECTION_TOKEN = new InjectionToken<string>('TOOLTIP_APPENDED_TO_INJECTION_TOKEN');

@Component({
    selector: 'ms-price-boost',
    templateUrl: './price-boost.component.html',
    changeDetection: ChangeDetectionStrategy.OnPush,
    providers: [
        optionContextServiceProviderFactory(true),
        {
            provide: BAB_TOOLTIP_HOST_INJECTION_TOKEN,
            useValue: 'ms-widget-slot',
        },
    ],
    // somewhere the dependency tree, there is a circular reference of the components,
    // even thought tried i was unable to figure out the loop
    imports: [forwardRef(() => GridListComponent), AsyncPipe],
})
export class PriceBoostComponent implements OnChanges, OnDestroy {
    private gridFactory = inject(ObservableGridFactory);
    private accessorService = inject(ModularConfigAccessorService);
    private competitionRoute = inject(CompetitionRouteService);
    private priceboostService = inject(PriceBoostService);

    @HostBinding('class') className = 'price-boost-hub';

    @Input() priceBoostPayload: CompetitionPriceBoostsPayload;
    @Input() widgetId: string | number;
    tracking = '';

    grid$: ObservableGrid;

    private gridOptions: GridOption;

    ngOnChanges(changes: ISimpleChanges<PriceBoostComponent>): void {
        if (changes.priceBoostPayload) {
            const payload = changes.priceBoostPayload.currentValue;
            const data = this.priceboostService.filterPriceBoostFixtures(payload.fixtures);

            this.gridOptions = {
                marketGrouping: true,
                subscriptionTopic: SubscriptionTopic.All,
                grouping: GridGrouping.None,
            };
            this.grid$?.destroy();
            this.grid$ = this.gridFactory.fromResponse(this.gridId, data, this.gridOptions, true, true);
            this.tracking = this.competitionRoute.getCompetitionRouteTracking(data.length > 0);
        }
    }

    ngOnDestroy(): void {
        this.grid$?.destroy();
    }

    private get gridId(): string {
        const parent = this.accessorService.getParentWidget();

        const id = parent ? `${parent.id}|${this.widgetId}` : `${this.widgetId}`;

        return id;
    }
}
